import type {
  Carrier,
  CarrierGeneralInfo,
  ConsentInfo,
  Contact,
  ContactGeneralInfo,
  TransportationModeEnum,
  Workspace,
} from '@apiTypes'
import {
  ConsentInfoReasonEnum,
  ConsentTypesEnum } from '@apiTypes'
import { isConsentGainedForContact } from '@/utils/privacy'
import type { Options } from '@store/options'
import type { RouteLocationNamedRaw } from 'vue-router'

export function buildCarrierContactsById(
  carriers: CarrierGeneralInfo[],
): Map<number, ContactGeneralInfo> {
  const contactsById = new Map<number, ContactGeneralInfo>()

  for (const carrier of carriers) {
    for (const contact of carrier.contacts) {
      contactsById.set(contact.id, contact)
    }
  }

  return contactsById
}

export function resolveCarrierContactsByIds(
  contactIds: number[] | undefined,
  contactsById: Map<number, ContactGeneralInfo>,
): ContactGeneralInfo[] {
  if (!contactIds?.length) {
    return []
  }

  return contactIds
    .map(id => contactsById.get(id))
    .filter((contact): contact is ContactGeneralInfo => contact != null)
}

export function isCarrierWithContactActiveForRequest(carrier: Carrier | CarrierGeneralInfo, contactId: number, mode: TransportationModeEnum | undefined): boolean {
  if (!carrier.isActive)
    return false

  const contact = carrier.contacts.find(c => c?.id === contactId)
  const contactModes = (contact?.transportationModes || [])

  return !!contact && isConsentGainedForContact(contact, ConsentTypesEnum.EMAIL) && (!mode || !contactModes.length || contactModes.includes(mode))
}

export function hasActiveCarrierForContacts(
  carriers: (Carrier | CarrierGeneralInfo)[],
  contactIds: number[] | undefined,
  mode: TransportationModeEnum | undefined,
): boolean {
  for (const contactId of contactIds ?? []) {
    const carrier = carriers.find(c => c.contacts.some(contact => contact.id === contactId))
    if (carrier && isCarrierWithContactActiveForRequest(carrier, contactId, mode))
      return true
  }

  return false
}

export function formatCarrierAddressWithoutStreet(carrier: Carrier | Workspace, countries: Options): string {
  return [carrier.address?.city || '', carrier.address?.postCode || '', countries[carrier.address?.country || ''] || ''].filter(part => !!part).join(', ')
}

export function carrierFindRelevantConsentInfo(carrier: Carrier, consentType: ConsentTypesEnum): ConsentInfo | undefined {
  const allConsents = carrier.contacts.reduce<ConsentInfo[]>((all, contact) => {
    all.push(...contact.consentStatuses.filter(info => info.consentType === consentType))

    return all
  }, [])

  const gained = allConsents.find(consent => consent.isGained)
  if (gained) return gained

  const priorityReasons: ConsentInfoReasonEnum[] = [
    ConsentInfoReasonEnum.REQUESTED,
    ConsentInfoReasonEnum.TOO_MANY_REQUESTS,
    ConsentInfoReasonEnum.NOT_REQUESTED,
  ]

  for (const reason of priorityReasons) {
    const byReason = allConsents.find(consent => consent.reason === reason)
    if (byReason) return byReason
  }

  return allConsents[0]
}

export function filterContactsByEmailConsentReason(contacts: Contact[], reasons: ConsentInfoReasonEnum[]): Contact[] {
  return contacts.filter(contact => {
    const emailConsent = contact.consentStatuses.find(consent => consent.consentType === ConsentTypesEnum.EMAIL)

    return emailConsent && reasons.includes(emailConsent.reason)
  })
}

export function carrierAuctionTo(chash: string, hash: string, opts?: { action?: string; panel?: string }): RouteLocationNamedRaw {
  const query: Record<string, string> = {}
  if (opts?.action) query.action = opts.action
  if (opts?.panel) query.panel = opts.panel

  return { name: 'carrier-chash-auction-hash', params: { chash, hash }, ...(Object.keys(query).length ? { query } : {}) }
}

export type CarrierTransportHashes = { hash?: string | null; contactHash?: string | null }

/** A booking's transport carries its auction hash as `hash`; an empty `contactHash` means no invited contact. */
export function carrierTransportAuctionHashes(
  transport: CarrierTransportHashes | null | undefined,
): CarrierAuctionHashes | null {
  if (!transport?.hash || !transport.contactHash)
    return null

  return { contactHash: transport.contactHash, auctionHash: transport.hash }
}

export function carrierTransportTo(transport: CarrierTransportHashes | null | undefined): RouteLocationNamedRaw | null {
  const hashes = carrierTransportAuctionHashes(transport)

  return hashes ? carrierAuctionTo(hashes.contactHash, hashes.auctionHash) : null
}

export type CarrierAuctionHashes = { contactHash: string; auctionHash: string }

export type CarrierBookingHashes = { contactHash: string; bookingHash: string }

export function carrierBookingHashes(
  booking: { hash: string; transport?: CarrierTransportHashes | null },
): CarrierBookingHashes | null {
  const contactHash = booking.transport?.contactHash

  return contactHash ? { contactHash, bookingHash: booking.hash } : null
}

export enum CarrierHashBookingRouteName {
  CREATE = 'carrier-chash-auction-hash-booking-create-waypoint',
  DETAIL = 'carrier-chash-booking-booking',
  EDIT = 'carrier-chash-booking-edit-booking',
}

export const CARRIER_BOOKING_LINK_ROUTE_NAME = 'carrier-booking-link-hash'
export const CARRIER_BOOKING_LINK_EDIT_ROUTE_NAME = 'carrier-booking-link-edit-hash'

export function carrierBookingLinkTo(hash: string): RouteLocationNamedRaw {
  return { name: CARRIER_BOOKING_LINK_ROUTE_NAME, params: { hash } }
}

export function carrierBookingLinkEditTo(hash: string): RouteLocationNamedRaw {
  return { name: CARRIER_BOOKING_LINK_EDIT_ROUTE_NAME, params: { hash } }
}

export function carrierHashBookingCreateTo(hashes: CarrierAuctionHashes, waypointId: number): RouteLocationNamedRaw {
  return {
    name: CarrierHashBookingRouteName.CREATE,
    params: { chash: hashes.contactHash, hash: hashes.auctionHash, waypoint: waypointId },
  }
}

export function carrierHashBookingTo(hashes: CarrierBookingHashes): RouteLocationNamedRaw {
  return {
    name: CarrierHashBookingRouteName.DETAIL,
    params: { chash: hashes.contactHash, booking: hashes.bookingHash },
  }
}

export function carrierHashBookingEditTo(hashes: CarrierBookingHashes): RouteLocationNamedRaw {
  return {
    name: CarrierHashBookingRouteName.EDIT,
    params: { chash: hashes.contactHash, booking: hashes.bookingHash },
  }
}
